(() => {
  const SELECTORS = {
    root: '[data-vgo-product]',
    config: '[data-vgo-config]',
    variants: '[data-vgo-variants]'
  };

  class VgoPremiumProduct {
    constructor(root) {
      if (root.dataset.vgoInitialized === 'true') return;
      root.dataset.vgoInitialized = 'true';

      this.root = root;
      this.config = this.readJSON(SELECTORS.config, {});
      this.variants = this.readJSON(SELECTORS.variants, []);
      this.currentVariant = this.variants.find((variant) => Number(variant.id) === Number(this.config.selectedVariantId)) || this.variants[0] || null;

      this.form = root.querySelector('[data-vgo-product-form]');
      this.variantInput = root.querySelector('[data-variant-id]');
      this.quantityInput = root.querySelector('[data-quantity]');
      this.addButton = root.querySelector('[data-add-to-cart]');
      this.addLabel = root.querySelector('[data-add-label]');
      this.buyButton = root.querySelector('[data-buy-now]');
      this.status = root.querySelector('[data-form-status]');
      this.stickyCart = root.querySelector('[data-sticky-cart]');
      this.stickyAddButton = root.querySelector('[data-sticky-add]');
      this.stickyAddLabel = root.querySelector('[data-sticky-add-label]');
      this.stickyPrice = root.querySelector('[data-sticky-price]');
      this.stickyImage = root.querySelector('[data-sticky-image]');
      this.giftMaster = root.querySelector('[data-gift-master]');
      this.giftBody = root.querySelector('[data-gift-body]');
      this.giftCheckbox = root.querySelector('[data-gift-checkbox]');
      this.tiers = [...root.querySelectorAll('[data-tier]')];
      this.optionGroups = [...root.querySelectorAll('[data-option-index]')];

      this.bindGallery();
      this.bindOptions();
      this.bindQuantity();
      this.bindGift();
      this.bindPurchase();
      this.bindStickyCart();
      this.bindDeliveryChecker();
      this.bindAccordions();
      this.bindCoupon();
      this.bindTiers();
      this.bindSizeGuide();
      this.updateAll();
    }

    readJSON(selector, fallback) {
      try {
        const node = this.root.querySelector(selector);
        return node ? JSON.parse(node.textContent) : fallback;
      } catch (error) {
        console.error('VGO product JSON error:', error);
        return fallback;
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0);
      const format = this.config.moneyFormat || '₹{{amount}}';
      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (Number.isNaN(number) || number == null) return '0';
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      };

      let value = '';
      if (format.includes('{{amount_no_decimals}}')) value = formatWithDelimiters(amount, 0);
      else if (format.includes('{{amount_with_comma_separator}}')) value = formatWithDelimiters(amount, 2, '.', ',');
      else if (format.includes('{{amount_no_decimals_with_comma_separator}}')) value = formatWithDelimiters(amount, 0, '.', ',');
      else value = formatWithDelimiters(amount, 2);

      return format
        .replace('{{amount_no_decimals}}', value)
        .replace('{{amount_with_comma_separator}}', value)
        .replace('{{amount_no_decimals_with_comma_separator}}', value)
        .replace('{{amount}}', value);
    }

    get quantity() {
      return Math.max(1, parseInt(this.quantityInput?.value || '1', 10) || 1);
    }

    setQuantity(value) {
      if (!this.quantityInput) return;
      this.quantityInput.value = Math.max(1, Number(value) || 1);
      this.updateSubtotal();
      this.updateTierState();
    }

    bindGallery() {
      this.slides = [...this.root.querySelectorAll('[data-vgo-slide]')];
      this.thumbs = [...this.root.querySelectorAll('[data-gallery-thumb]')];
      this.activeMediaIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));

      this.root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.showMediaByIndex(this.activeMediaIndex - 1));
      this.root.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showMediaByIndex(this.activeMediaIndex + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showMediaById(thumb.dataset.mediaId)));

      const zoomModal = this.root.querySelector('[data-zoom-modal]');
      const zoomImage = this.root.querySelector('[data-zoom-image]');
      const closeZoom = () => {
        if (!zoomModal) return;
        zoomModal.hidden = true;
        document.documentElement.style.overflow = '';
      };

      this.root.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
        const activeSlide = this.slides[this.activeMediaIndex];
        const src = activeSlide?.dataset.zoomSrc;
        if (!src || !zoomModal || !zoomImage) return;
        zoomImage.src = src;
        zoomImage.alt = activeSlide.dataset.zoomAlt || '';
        zoomModal.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      });
      this.root.querySelector('[data-zoom-close]')?.addEventListener('click', closeZoom);
      zoomModal?.addEventListener('click', (event) => { if (event.target === zoomModal) closeZoom(); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && zoomModal && !zoomModal.hidden) closeZoom(); });
    }

    showMediaByIndex(index) {
      if (!this.slides.length) return;
      const normalized = (index + this.slides.length) % this.slides.length;
      this.activeMediaIndex = normalized;
      const mediaId = this.slides[normalized].dataset.mediaId;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalized;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      this.thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });

      const zoomButton = this.root.querySelector('[data-gallery-zoom]');
      if (zoomButton) zoomButton.hidden = !this.slides[normalized]?.dataset.zoomSrc;
    }

    showMediaById(mediaId) {
      const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) this.showMediaByIndex(index);
    }


    bindOptions() {
      this.optionGroups.forEach((group) => {
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          button.addEventListener('click', () => {
            group.querySelectorAll('[data-option-value]').forEach((item) => {
              const selected = item === button;
              item.classList.toggle('is-selected', selected);
              item.setAttribute('aria-pressed', selected ? 'true' : 'false');
            });
            this.resolveVariant();
          });
        });
      });
    }

    selectedOptions() {
      return this.optionGroups.map((group) => group.querySelector('.is-selected[data-option-value]')?.dataset.optionValue || '');
    }

    resolveVariant() {
      const options = this.selectedOptions();
      const match = this.variants.find((variant) => variant.options.every((option, index) => option === options[index]));
      if (match) {
        this.currentVariant = match;
        this.updateAll();
        this.updateURL(match.id);
        this.root.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: match } }));
      }
      this.updateOptionAvailability();
    }

    updateOptionAvailability() {
      const selected = this.selectedOptions();
      this.optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const candidate = [...selected];
          candidate[groupIndex] = button.dataset.optionValue;
          const available = this.variants.some((variant) => variant.available && variant.options.every((option, index) => option === candidate[index]));
          button.classList.toggle('is-unavailable', !available);
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
      });
    }

    updateURL(variantId) {
      if (!history.replaceState || !variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      history.replaceState({}, '', url.toString());
    }

    updateAll() {
      if (!this.currentVariant) return;
      if (this.variantInput) this.variantInput.value = this.currentVariant.id;
      this.updatePrice();
      this.updateAvailability();
      this.updateDimensions();
      this.updateSizeGuide();
      this.updateSubtotal();
      this.updateTierPrices();
      this.updateTierState();
      this.updateOptionAvailability();
      if (this.stickyImage && this.currentVariant.featuredImage) {
        this.stickyImage.src = this.currentVariant.featuredImage;
      }
      if (this.currentVariant.featuredMediaId) this.showMediaById(this.currentVariant.featuredMediaId);
    }

    updatePrice() {
      const current = this.root.querySelector('[data-current-price]');
      const compare = this.root.querySelector('[data-compare-price]');
      const discount = this.root.querySelector('[data-discount-percent]');
      if (current) current.textContent = this.formatMoney(this.currentVariant.price);
      if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(this.currentVariant.price);

      const hasCompare = Number(this.currentVariant.compareAtPrice) > Number(this.currentVariant.price);
      if (compare) {
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? this.formatMoney(this.currentVariant.compareAtPrice) : '';
      }
      if (discount) {
        discount.hidden = !hasCompare;
        discount.textContent = hasCompare
          ? `${Math.round(((this.currentVariant.compareAtPrice - this.currentVariant.price) / this.currentVariant.compareAtPrice) * 100)}% off`
          : '';
      }
    }

    updateAvailability() {
      const available = Boolean(this.currentVariant.available);
      [this.addButton, this.buyButton, this.stickyAddButton].forEach((button) => { if (button) button.disabled = !available; });
      if (this.addLabel) this.addLabel.textContent = available ? this.config.availableText : this.config.soldOutText;
      if (this.stickyAddLabel) this.stickyAddLabel.textContent = available ? (this.config.stickyCartButtonText || this.config.availableText) : this.config.soldOutText;
      const sku = this.root.querySelector('[data-sku]');
      if (sku) sku.textContent = this.currentVariant.sku || '—';
    }

    updateDimensions() {
      const title = this.root.querySelector('[data-dimension-variant-title]');
      const image = this.root.querySelector('[data-dimension-image]');
      const placeholder = this.root.querySelector('[data-dimension-placeholder]');
      const height = this.root.querySelector('[data-dimension-height]');
      const width = this.root.querySelector('[data-dimension-width]');
      const weight = this.root.querySelector('[data-dimension-weight]');
      const dimensionImage = this.currentVariant.dimensionImage || '';

      if (title) title.textContent = this.currentVariant.sizeTitle || this.currentVariant.title;
      if (height) height.textContent = this.currentVariant.height || '—';
      if (width) width.textContent = this.currentVariant.width || '—';
      if (weight) weight.textContent = this.currentVariant.weight || '—';

      if (image) {
        image.hidden = !dimensionImage;
        if (dimensionImage) {
          image.src = dimensionImage;
          image.removeAttribute('srcset');
        } else {
          image.removeAttribute('src');
          image.removeAttribute('srcset');
        }
      }
      if (placeholder) placeholder.hidden = Boolean(dimensionImage);
    }

    updateSizeGuide() {
      const title = this.root.querySelector('[data-size-title]');
      const description = this.root.querySelector('[data-size-description]');
      if (title) title.textContent = this.currentVariant.sizeTitle || this.currentVariant.title;
      if (description) description.textContent = this.currentVariant.sizeDescription || '';
      const firstOption = this.currentVariant.options[0];
      this.root.querySelectorAll('[data-size-choice]').forEach((button) => button.classList.toggle('is-active', button.dataset.sizeChoice === firstOption));
    }

    bindQuantity() {
      this.root.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.setQuantity(this.quantity - 1));
      this.root.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.setQuantity(this.quantity + 1));
      this.quantityInput?.addEventListener('change', () => this.setQuantity(this.quantity));
      this.quantityInput?.addEventListener('input', () => {
        this.updateSubtotal();
        this.updateTierState();
      });
    }

    bindGift() {
      const giftWrap = this.root.querySelector('[data-gift-wrap]');
      if (!giftWrap || !this.giftMaster) return;

      const syncMasterState = (clearChild = false) => {
        const open = Boolean(this.giftMaster.checked);
        giftWrap.classList.toggle('is-open', open);
        this.giftMaster.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (this.giftBody) this.giftBody.hidden = !open;

        if (!open && clearChild && this.giftCheckbox?.checked) {
          this.giftCheckbox.checked = false;
        }

        giftWrap.classList.toggle('has-gift-selected', Boolean(this.giftCheckbox?.checked));
        this.updateSubtotal();
      };

      this.giftMaster.addEventListener('change', () => syncMasterState(true));
      this.giftCheckbox?.addEventListener('change', () => {
        giftWrap.classList.toggle('has-gift-selected', this.giftCheckbox.checked);
        this.updateSubtotal();
      });

      syncMasterState(false);
    }

    giftSelected() {
      return Boolean(this.giftCheckbox?.checked && this.config.giftVariantId);
    }

    updateSubtotal() {
      if (!this.currentVariant) return;
      const subtotal = (Number(this.currentVariant.price) * this.quantity) + (this.giftSelected() ? Number(this.config.giftPrice || 0) : 0);
      const target = this.root.querySelector('[data-subtotal]');
      if (target) target.textContent = this.formatMoney(subtotal);
      const suffix = this.root.querySelector('[data-subtotal-suffix]');
      if (suffix) suffix.hidden = !this.giftSelected();
    }

    bindTiers() {
      this.tiers.forEach((tier) => {
        tier.addEventListener('click', () => this.setQuantity(Number(tier.dataset.min || 1)));
      });
    }

    updateTierPrices() {
      if (!this.currentVariant) return;
      this.root.querySelectorAll('[data-tier-price]').forEach((node) => {
        const discount = Number(node.dataset.discount || 0);
        const price = Math.round(Number(this.currentVariant.price) * (100 - discount) / 100);
        node.textContent = this.formatMoney(price);
      });
    }

    activeTierForQuantity(quantity = this.quantity) {
      return this.tiers.find((tier) => quantity >= Number(tier.dataset.min || 1) && quantity <= Number(tier.dataset.max || 9999)) || this.tiers[0];
    }

    updateTierState() {
      if (!this.tiers.length) return;
      const quantity = this.quantity;
      const active = this.activeTierForQuantity(quantity);
      this.tiers.forEach((tier) => tier.classList.toggle('is-active', tier === active));

      const hint = this.root.querySelector('[data-tier-hint]');
      if (!hint) return;
      const upcoming = this.tiers
        .map((tier) => ({ tier, min: Number(tier.dataset.min || 1), discount: Number(tier.dataset.discount || 0) }))
        .filter((item) => item.min > quantity)
        .sort((a, b) => a.min - b.min)[0];
      if (upcoming) {
        const needed = upcoming.min - quantity;
        hint.textContent = `Add ${needed} more ${needed === 1 ? 'piece' : 'pieces'} and save ${upcoming.discount}% on each`;
      } else {
        hint.textContent = 'Best value unlocked — your highest quantity saving is active.';
      }
    }

    activeDiscountCode() {
      return this.activeTierForQuantity()?.dataset.code?.trim() || '';
    }

    bindPurchase() {
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addItems('cart');
      });
      this.buyButton?.addEventListener('click', () => this.addItems('checkout'));
    }

    async openCartDrawerAfterAdd(responseData) {
      const customDrawer =
        document.querySelector('[data-vgoxcd28-cart-shell], .vgoxcd28-cart-shell');

      /*
        VGO Cart Drawer V3/V4/V5 bridge.
        These drawer versions listen to vgo:open-cart and refresh themselves
        from the CURRENT Shopify cart, so the newly added product is rendered
        before/while the drawer opens.
      */
      if (customDrawer) {
        document.dispatchEvent(
          new CustomEvent('vgo:open-cart', {
            bubbles: true,
            detail: {
              source: 'vgo-premium-product',
              response: responseData
            }
          })
        );

        // Compatibility sync for drawer versions where opening/syncing are separate.
        document.dispatchEvent(
          new CustomEvent('vgo:sync-cart-drawer', {
            bubbles: true,
            detail: {
              source: 'vgo-premium-product',
              response: responseData
            }
          })
        );

        // Give the drawer a moment to rebuild line items from /cart.js,
        // then broadcast the generic update event with the full current cart.
        await new Promise((resolve) => window.setTimeout(resolve, 120));

        try {
          const cartResponse = await fetch(this.config.cartUrl ? `${this.config.cartUrl}.js` : '/cart.js', {
            headers: { Accept: 'application/json' }
          });
          const cart = await cartResponse.json();

          document.dispatchEvent(
            new CustomEvent('vgo:cart-updated', {
              bubbles: true,
              detail: {
                cart,
                response: responseData,
                source: 'vgo-premium-product'
              }
            })
          );

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                cart,
                response: responseData,
                source: 'vgo-premium-product'
              }
            })
          );
        } catch (error) {
          console.warn('VGO cart state refresh failed:', error);
        }

        return true;
      }

      /*
        Legacy VGO drawer bridge.
        Older versions can still open through vgo:open-cart.
      */
      const legacyVgoDrawer = document.querySelector('.vgo-cart-shell');
      if (legacyVgoDrawer) {
        document.dispatchEvent(
          new CustomEvent('vgo:open-cart', {
            bubbles: true,
            detail: {
              source: 'vgo-premium-product',
              response: responseData
            }
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              response: responseData,
              source: 'vgo-premium-product'
            }
          })
        );

        return true;
      }

      /*
        Dawn / standard Shopify drawer fallback.
        Open the drawer when one is mounted.
      */
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        try {
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            cartDrawer.classList.add('active', 'is-open');
            cartDrawer.setAttribute('open', '');
          }
        } catch (error) {
          console.warn('Cart drawer open failed:', error);
        }

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              response: responseData,
              source: 'vgo-premium-product'
            }
          })
        );

        return true;
      }

      return false;
    }

    async addItems(destination) {
      if (!this.currentVariant?.available) return;
      const properties = {};
      if (this.giftSelected()) properties[this.config.giftPropertyName || 'Make it a gift'] = 'Yes';

      const items = [{ id: Number(this.currentVariant.id), quantity: this.quantity, properties }];
      if (this.giftSelected()) {
        items.push({
          id: Number(this.config.giftVariantId),
          quantity: 1,
          properties: {
            _gift_for_product: this.config.productTitle,
            _gift_for_handle: this.config.productHandle
          }
        });
      }

      this.setLoading(true);
      this.setStatus('');
      try {
        const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this product.');

        const code = this.activeDiscountCode();
        if (destination === 'checkout') {
          window.location.href = code ? `/discount/${encodeURIComponent(code)}?redirect=/checkout` : '/checkout';
          return;
        }

        // ADD TO CART: stay on the product page, refresh the live cart,
        // and open the cart drawer with the newly added product visible.
        const drawerOpened = await this.openCartDrawerAfterAdd(data);

        this.setStatus('Added to cart successfully.');
        this.setLoading(false);

        // Only fall back to the cart page when no drawer exists at all.
        if (!drawerOpened) {
          const cartUrl = this.config.cartUrl || '/cart';
          window.location.href = code
            ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`
            : cartUrl;
        }

        return;
      } catch (error) {
        this.setStatus(error.message || 'Something went wrong.', true);
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      [this.addButton, this.buyButton, this.stickyAddButton].forEach((button) => {
        if (button) button.disabled = loading || !this.currentVariant?.available;
      });
      if (this.addLabel) this.addLabel.textContent = loading ? 'ADDING…' : (this.currentVariant?.available ? this.config.availableText : this.config.soldOutText);
      if (this.stickyAddLabel) {
        this.stickyAddLabel.textContent = loading
          ? 'ADDING…'
          : (this.currentVariant?.available ? (this.config.stickyCartButtonText || this.config.availableText) : this.config.soldOutText);
      }
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.style.color = error ? '#b83a05' : '';
    }

    bindStickyCart() {
      if (!this.stickyCart) return;

      this.stickyAddButton?.addEventListener('click', () => this.addItems('cart'));

      const infoScroller = this.root.querySelector('.vgo-product__info-column');
      let ticking = false;

      const updateVisibility = () => {
        ticking = false;

        const rootRect = this.root.getBoundingClientRect();
        const anchorRect = this.addButton?.getBoundingClientRect();
        const scrollerRect = infoScroller?.getBoundingClientRect();

        const visibleTop = scrollerRect && scrollerRect.bottom > 0 && scrollerRect.top < window.innerHeight
          ? Math.max(0, scrollerRect.top)
          : 0;
        const visibleBottom = scrollerRect && scrollerRect.bottom > 0 && scrollerRect.top < window.innerHeight
          ? Math.min(window.innerHeight, scrollerRect.bottom)
          : window.innerHeight;

        const primaryButtonVisible = anchorRect
          ? anchorRect.bottom > visibleTop && anchorRect.top < visibleBottom
          : false;

        const primaryButtonPassed = anchorRect
          ? anchorRect.bottom <= visibleTop + 2
          : false;

        // Once the main product section has been passed, keep the quick-add bar
        // visible throughout reviews, FAQ, related products and the rest of the
        // product page. Before that, only show it after the main Add to Cart has
        // actually scrolled above the visible product-info area.
        const mainProductPassed = rootRect.bottom <= 80;
        const shouldShow = !primaryButtonVisible && (primaryButtonPassed || mainProductPassed);

        this.stickyCart.classList.toggle('is-visible', shouldShow);
      };

      const schedule = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(updateVisibility);
      };

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);

      setTimeout(updateVisibility, 60);
    }

    bindDeliveryChecker() {
      const input = this.root.querySelector('[data-pincode-input]');
      const button = this.root.querySelector('[data-pincode-check]');
      const result = this.root.querySelector('[data-pincode-result]');
      if (!input || !button || !result) return;

      const check = () => {
        const pincode = input.value.replace(/\D/g, '').slice(0, 6);
        input.value = pincode;
        result.hidden = false;
        if (!/^\d{6}$/.test(pincode)) {
          result.classList.add('is-error');
          result.textContent = 'Please enter a valid 6-digit Indian pincode.';
          return;
        }
        result.classList.remove('is-error');
        const message = String(this.config.deliveryMessage || 'Estimated delivery in {min}-{max} business days')
          .replace('{min}', this.config.deliveryMinDays)
          .replace('{max}', this.config.deliveryMaxDays)
          .replace('{pincode}', pincode);
        result.textContent = message;
      };
      button.addEventListener('click', check);
      input.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); check(); } });
    }

    bindAccordions() {
      this.root.querySelectorAll('[data-accordion]').forEach((accordion) => {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        const content = accordion.querySelector('[data-accordion-content]');
        trigger?.addEventListener('click', () => {
          const open = !accordion.classList.contains('is-open');
          accordion.classList.toggle('is-open', open);
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (content) content.hidden = !open;
        });
      });
    }

    bindCoupon() {
      this.root.querySelector('[data-copy-coupon]')?.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        const code = button.dataset.copyCoupon;
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          const oldLabel = button.getAttribute('aria-label');
          button.setAttribute('aria-label', 'Copied');
          setTimeout(() => button.setAttribute('aria-label', oldLabel || 'Copy coupon code'), 1500);
        } catch (error) {
          console.warn('Coupon copy failed', error);
        }
      });
    }

    bindSizeGuide() {
      this.root.querySelectorAll('[data-size-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          const matchingTopButton = this.optionGroups[0]?.querySelector(`[data-option-value="${CSS.escape(button.dataset.sizeChoice)}"]`);
          matchingTopButton?.click();
        });
      });
    }
  }

  const init = (scope = document) => scope.querySelectorAll(SELECTORS.root).forEach((root) => new VgoPremiumProduct(root));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();